import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, FileDown, FileX2 } from "lucide-react";

import { EXPORT_SCOPES, PASS_CHOICES, EXPORT_STATE_TEXT, scopeText, periodText, exportPeriods, exportErrors, normalizeExport, normalizeExports, exportLine,
  circleText, grantsText } from "@/lib/v3Settings";
import { deletionCapabilities, deletionImpact, deletionRequest } from "@/lib/memberAccount";
import { normalizePassGrants } from "@/lib/v3Pass";
import { shortName } from "@/lib/v3People";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { useDeletionRequest } from "../useDeletionRequest";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, V3Field, V3PasswordField, V3Check, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, V3Select, V3_LINK } from "../components/v3app";
import { InfoBox } from "../components/v3people";
import { Blocked } from "../components/v3plans";

const Aside = ({ text: body, title = "Ausgewählter Vorgang", testid }) => <><AreaCard title={title} text={body} testid={testid} /><ResponsibleCard /></>;

// M98: the request needs POST /member/account/export (D-93); a known earlier request opens M116 (GET …/exports, D-93).
function ExportForm() {
  const periods = exportPeriods();
  const earlier = useCore("/member/account/exports", (d) => ({ list: normalizeExports(d) }));
  const [form, setForm] = useState({ scope: EXPORT_SCOPES[0][0], period: periods[0][0], pass: "no", confirmed: false });
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setReview(false); };
  const pick = (k) => (e) => set(k)(e.target.value);
  const check = () => { const e = exportErrors(form, periods); setErrors(e); setReview(!Object.keys(e).length); };
  const last = earlier.data?.list?.[0] || null;
  return (
    <V3Columns layout="app" aside={<Aside text={scopeText(form.scope)} testid="export-area" />} main={<div data-m="M98">
      <V3Select id="export-scope" label="Umfang" value={form.scope} onChange={pick("scope")} testid="export-scope" error={errors.scope}>
        {EXPORT_SCOPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </V3Select>
      <V3Select id="export-period" label="Zeitraum" value={form.period} onChange={pick("period")} testid="export-period" error={errors.period}>
        {periods.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </V3Select>
      <V3Select id="export-pass" label="Privater Access Pass" value={form.pass} onChange={pick("pass")} testid="export-pass" error={errors.pass} hint="Dein Access Pass gehört nur dazu, wenn du ihn ausdrücklich auswählst.">
        {PASS_CHOICES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </V3Select>
      <V3Field id="export-identity" label="Identität" value="Vor Bereitstellung bestätigen" readOnly hint="Vor dem Herunterladen bestätigst du noch einmal, dass du es bist." />
      <V3Check id="export-confirm" checked={form.confirmed} onChange={set("confirmed")} error={errors.confirmed}>Ausgewählten Datenumfang vor Bereitstellung prüfen</V3Check>
      {last && <DetailList><DetailRow label="Letzte Anfrage" value={exportLine(last)} to={`/einstellungen/daten/${encodeURIComponent(last.id)}`} testid="export-last" /></DetailList>}
      {review && <div className="mt-[11px]"><InfoBox title="Datenanfrage prüfen" testid="export-review"
        text={`${scopeText(form.scope)} · ${periodText(form.period)} · Access Pass ${form.pass === "yes" ? "ausgewählt" : "nicht ausgewählt"}. Bereitgestellt würde nur für dich, nach erneuter Bestätigung deiner Identität.`} /></div>}
      {review && <Blocked id="export-blocked">Datenanfragen sind im gemeinsamen Core noch nicht eingerichtet. Es wird keine Anfrage gesendet und nichts zusammengestellt.</Blocked>}
      <V3Actions>
        {review ? <button type="button" disabled aria-describedby="export-blocked" data-testid="export-send" className={V3_PRIMARY}>Datenanfrage senden</button>
          : <button type="button" onClick={check} data-testid="export-check" className={V3_PRIMARY}>Datenanfrage prüfen</button>}
        {review ? <button type="button" onClick={() => setReview(false)} data-testid="export-edit" className={V3_SECONDARY}>Ändern</button>
          : <Link to="/einstellungen" data-testid="export-cancel" className={V3_SECONDARY}>Abbrechen</Link>}
      </V3Actions>
    </div>} />
  );
}

export function DataExport() {
  return (
    <div data-testid="export-page">
      <V3Title title="Eigene Daten anfordern" intro="Fordere eine Zusammenstellung deiner eigenen Daten an." testid="export-title" />
      <ExportForm />
    </div>
  );
}

// M116: state of one own export (GET /member/account/export/{id}, D-94); downloading needs an identity check that is not available.
function ExportState({ id }) {
  const res = useCore(`/member/account/export/${encodeURIComponent(id)}`, (d) => ({ x: normalizeExport(d) }));
  if (res.error && !["notfound", "gone"].includes(res.error)) return <ConnectionError onRetry={res.reload} />;
  if (!res.data && !res.error) return <Loading />;
  const x = res.data?.x || null;
  const back = <Link to="/einstellungen/daten" data-testid="export-state-back" className={V3_SECONDARY}>Zurück</Link>;
  if (!x) return (
    <V3Columns layout="detail" aside={<Aside title="Dein Bereich" text="Kein Export verfügbar" testid="export-state-area" />} main={<>
      <StatusBanner tone="sand" icon={FileX2} title={res.error === "gone" ? "Export abgelaufen" : "Export nicht verfügbar"}
        text={res.error === "gone" ? "Dieser Export kann nicht mehr heruntergeladen werden." : "Zu dieser Anfrage liefert der gemeinsame Core keinen Stand."} testid="export-state-missing" />
      <V3Actions><Link to="/einstellungen/daten" data-testid="export-state-new" className={V3_PRIMARY}>Zur Datenanfrage</Link></V3Actions>
    </>} />
  );
  return (
    <V3Columns layout="detail" aside={<Aside title="Dein Bereich" text={scopeText(x.scope)} testid="export-state-area" />} main={<>
      <StatusBanner tone={x.state === "ready" ? "mint" : "sand"} icon={x.state === "ready" ? Check : FileDown} title={x.state === "ready" ? "Export bereit" : `Export · ${EXPORT_STATE_TEXT[x.state]}`} text={scopeText(x.scope)} testid="export-state-banner" />
      <DetailList>
        <DetailRow label="Export" value={scopeText(x.scope)} testid="export-state-scope" />
        <DetailRow label="Zeitraum" value={periodText(x.period)} testid="export-state-period" />
        <DetailRow label="Status" value={EXPORT_STATE_TEXT[x.state]} testid="export-state-status" />
        <DetailRow label="Zugriff" value="Identität vor dem Herunterladen bestätigen" testid="export-state-access" />
      </DetailList>
      <Blocked id="export-state-blocked">Das Herunterladen mit Identitätsprüfung ist im gemeinsamen Core noch nicht eingerichtet. Es wird nichts heruntergeladen.</Blocked>
      <V3Actions>
        <button type="button" disabled aria-describedby="export-state-blocked" data-testid="export-state-identity" className={V3_PRIMARY}>Identität bestätigen</button>
        {back}
      </V3Actions>
    </>} />
  );
}

export function ExportStatus() {
  const { id } = useParams();
  return (
    <div data-m="M116" data-testid="export-state-page">
      <V3Title title="Export zum Herunterladen bereit" intro="Den Stand deiner angeforderten Zusammenstellung siehst du hier." testid="export-state-title" />
      <ExportState key={id} id={id} />
    </div>
  );
}

// M99: circle responsibilities need GET /member/account/deletion-impact (D-95); own grants come from GET /member/access-pass/requests (Core 1.1).
function DeleteImpact() {
  const { member } = useMemberAuth();
  const caps = useCore("/member/account/capabilities", (d) => ({ ready: deletionCapabilities(d) }));
  const impact = useCore("/member/account/deletion-impact", (d) => {
    const v = deletionImpact(d); if (!v) throw new Error("Invalid impact"); return { v };
  });
  const pending = useCore("/member/account/deletion-request", (d) => {
    const v = deletionRequest(d); if (!v) throw new Error("Invalid request"); return v;
  });
  const grants = useCore("/member/access-pass/requests", (d) => ({ list: normalizePassGrants(d) }));
  const submission = useDeletionRequest();
  const [review, setReview] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const name = shortName(member?.name) || "Dein Konto";
  const resources = [caps, impact, pending];
  const unavailable = resources.some((r) => r.error === "notfound") || (caps.data && !caps.data.ready);
  if (!unavailable && resources.some((r) => r.error)) return <ConnectionError onRetry={() => resources.forEach((r) => r.reload())} />;
  if (!unavailable && resources.some((r) => !r.data)) return <Loading />;
  const imp = impact.data?.v;
  const gr = grants.error ? null : grants.data ? grants.data.list : undefined;
  const existing = submission.request || pending.data?.request;
  const ready = !unavailable && /^[a-f0-9]{24}$/.test(member?.id || "");
  const send = () => {
    if (!ready || submission.busy || existing) return;
    if (!password) { setPasswordError("Bitte bestätige dein Passwort."); return; }
    const value = password; setPassword(""); setPasswordError(""); submission.send(value);
  };
  return (
    <V3Columns layout="detail" aside={<Aside text={name} testid="delete-area" />} main={<div data-m="M99">
      <StatusBanner tone={existing ? "mint" : "notice"} title={existing ? "Löschanfrage eingegangen" : "Deine Bestätigung ist erforderlich"}
        text={existing ? "Deine Anfrage liegt zur Prüfung vor. Dein Konto ist weiterhin vorhanden; es wurde noch nichts gelöscht." : "Prüfe den angezeigten Vorgang und die konkreten Auswirkungen."} testid="delete-confirm" />
      <DetailList>
        <DetailRow label="Konto" value={name} testid="delete-account" />
        <DetailRow label="Kreisverantwortung" value={circleText(unavailable ? null : imp)} testid="delete-circles" />
        <DetailRow label="Gastgeber-Aufgaben" value={imp ? `${imp.hosted} offene Zuordnungen` : "Noch nicht verfügbar"} testid="delete-hosted" />
        <DetailRow label="Eigene Freigaben" value={grantsText(gr)} to="/access-pass/freigaben" testid="delete-grants" />
        <DetailRow label="Status" value={existing ? "Anfrage eingegangen · Prüfung offen" : submission.phase === "unclear" ? "Ausgang noch unklar" : "Keine offene Anfrage bestätigt"} testid="delete-status" />
      </DetailList>
      <Link to="/einstellungen/daten" data-testid="delete-export" className={`${V3_LINK} mt-[6px]`}>Vorher eine Kopie deiner Daten anfordern</Link>
      {unavailable && <Blocked id="delete-blocked">Löschanfragen sind in diesem Core noch nicht verfügbar. Es wird nichts gesendet.</Blocked>}
      {review && !existing && ready && <>
        <InfoBox title="Löschanfrage prüfen" testid="delete-review" text="Mit deinem Passwort bestätigst du die Anfrage zur Prüfung. Dein Konto wird damit noch nicht geschlossen oder gelöscht. Offene Verantwortungen müssen vor dem späteren Abschluss geklärt werden." />
        <fieldset disabled={submission.busy} className="mt-[18px]">
          <V3PasswordField id="delete-password" label="Aktuelles Passwort" value={password} onChange={(v) => { setPassword(v); setPasswordError(""); }}
            autoComplete="current-password" error={passwordError} hint="Nur zur Bestätigung dieser Anfrage; es wird nicht gespeichert." />
        </fieldset>
      </>}
      {submission.reason && <div role="status" aria-live="polite" data-testid="delete-result"><InfoBox title={submission.phase === "unclear" ? "Stand noch unklar" : "Anfrage nicht bestätigt"} text={submission.reason} /></div>}
      {submission.phase === "found" && <InfoBox title="Offene Anfrage gefunden" text="Der gemeinsame Core bestätigt eine offene Anfrage. Ob der letzte Sendeversuch sie erfasst hat, konnte nicht bestätigt werden." testid="delete-found" />}
      {submission.phase === "confirmed" && <div role="status" aria-live="polite" data-testid="delete-success">Die Anfrage wurde angenommen und ihr Eingang erneut bestätigt.</div>}
      <V3Actions>
        {!existing && (review ? <button type="button" onClick={send} disabled={!ready || submission.busy} aria-describedby={unavailable ? "delete-blocked" : undefined} data-testid="delete-send" className={V3_PRIMARY}>{submission.busy ? "Wird geprüft …" : "Löschanfrage senden"}</button>
          : <button type="button" disabled={!ready} onClick={() => setReview(true)} data-testid="delete-check" className={V3_PRIMARY}>Löschanfrage prüfen</button>)}
        {ready && <button type="button" disabled={submission.busy} onClick={submission.check} data-testid="delete-refresh" className={V3_SECONDARY}>Stand prüfen</button>}
        <Link to="/einstellungen" data-testid="delete-keep" className={V3_SECONDARY}>{existing ? "Zurück zu Einstellungen" : "Konto behalten"}</Link>
      </V3Actions>
    </div>} />
  );
}

export function AccountDelete() {
  const { member } = useMemberAuth();
  return (
    <div data-testid="delete-page">
      <V3Title title="Konto löschen: Auswirkungen prüfen" intro="Prüfe offene Verantwortungen und deine Freigaben vor einer Löschanfrage." testid="delete-title" />
      <DeleteImpact key={member?.id || "signed-out"} />
    </div>
  );
}
